import React, { useState, useEffect } from "react";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import PersonaManagementTable from "../personaRegistrations/PersonaManagementTable";
import PersonaRegistrationsForm from "../personaRegistrations/PersonaRegistrationsForm";
import "../../assets/css/reportRegistrations.css";
import PageHeader from "../layout/PageHeader";
import TabStrip from "../layout/TabStrip";
import RankedTable from "../layout/RankedTable";
import SingleFieldEditModal from "../layout/SingleFieldEditModal";
import MapReportsModal from "../layout/MapReportsModal";
import ToastNotification from "../layout/ToastNotification";
import ConfirmDialog from "../layout/ConfirmDialog";
import { usePersonaAdmin } from "../../hooks/usePersonaAdmin";
import { useReportAdmin } from "../../hooks/useReportAdmin";
import AppButton from "../layout/AppButton";

const PersonaAdministration = () => {
  const {
    fetchPersonas: fetchRawPersonas,
    createPersona: createPersonaApi,
    updatePersona: updatePersonaApi,
    deletePersona: deletePersonaApi,
    fetchMappedReports,
    saveReportMapping,
    clearError: clearPersonaError,
    error: personaError,
  } = usePersonaAdmin();
  const { fetchReports: fetchRawReports } = useReportAdmin();
  const [activeTab, setActiveTab] = useState("management");
  const [searchText, setSearchText] = useState("");
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPersonaData, setEditPersonaData] = useState(null);
  const [showMapReportModal, setShowMapReportModal] = useState(false);
  const [selectedPersonaForMapping, setSelectedPersonaForMapping] =
    useState(null);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [mapReportSearchText, setMapReportSearchText] = useState("");
  const [availableReports, setAvailableReports] = useState([]);
  const [loadingMappedReports, setLoadingMappedReports] = useState(false);
  const [creatingPersona, setCreatingPersona] = useState(false);
  const [updatingPersona, setUpdatingPersona] = useState(false);
  const [deletingPersona, setDeletingPersona] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    personaId: null,
  });

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  // Fetch personas
  const loadPersonas = async () => {
    try {
      setLoading(true);
      const data = await fetchRawPersonas();
      const raw = data?.data ?? (Array.isArray(data) ? data : []);
      setPersonas(
        raw.map((persona) => ({
          id: persona.userGroupId,
          name: persona.personaName,
          createdBy: persona.createdBy,
          createdAt: formatDateFromISO(persona.createdAt),
          mappedReportIds: [],
        })),
      );
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available reports
  useEffect(() => {
    const loadReports = async () => {
      try {
        const data = await fetchRawReports();
        const raw = data?.data ?? (Array.isArray(data) ? data : []);
        setAvailableReports(
          raw.map((r) => ({ id: r.id, name: r.report_name })),
        );
      } catch (err) {
        console.error("Error fetching reports:", err);
      }
    };
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateFromISO = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const filteredPersonas = personas.filter((persona) =>
    persona.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleCreatePersona = async (name) => {
    const isDuplicate = personas.some(
      (persona) => persona.name.toLowerCase() === name.toLowerCase(),
    );
    if (isDuplicate) {
      showToast("A persona with this name already exists.", "warning");
      return;
    }
    try {
      setCreatingPersona(true);
      await createPersonaApi({ personaName: name });
      await loadPersonas();
      setActiveTab("management");
      showToast("Persona created successfully!");
    } catch (err) {
      showToast("Error creating persona: " + err.message, "error");
    } finally {
      setCreatingPersona(false);
    }
  };

  const handleDeletePersona = (id) => {
    if (!id) {
      showToast("Invalid persona ID", "error");
      return;
    }
    setConfirmDialog({ open: true, personaId: id });
  };

  const handleConfirmDelete = async () => {
    const id = confirmDialog.personaId;
    try {
      setDeletingPersona(true);
      await deletePersonaApi(id);
      await loadPersonas();
      showToast("Persona deleted successfully!");
      setConfirmDialog({ open: false, personaId: null });
    } catch (err) {
      showToast("Error deleting persona: " + err.message, "error");
    } finally {
      setDeletingPersona(false);
    }
  };

  const handleEditPersona = (persona) => {
    setEditPersonaData(persona);
    setShowEditModal(true);
  };


  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditPersonaData(null);
  };

  const handleUpdatePersona = async (event) => {
    event.preventDefault();
    if (!editPersonaData) return;
    try {
      setUpdatingPersona(true);
      await updatePersonaApi(editPersonaData.id, {
        newName: editPersonaData.name,
      });
      await loadPersonas();
      handleCloseEditModal();
      showToast("Persona updated successfully!");
    } catch (err) {
      showToast("Error updating persona: " + err.message, "error");
    } finally {
      setUpdatingPersona(false);
    }
  };

  const handleAddReport = async (persona) => {
    setSelectedPersonaForMapping(persona);
    setSelectedReportIds([]);
    setMapReportSearchText("");
    setShowMapReportModal(true);
    try {
      setLoadingMappedReports(true);
      const ids = await fetchMappedReports(persona.id);
      setSelectedReportIds(ids);
    } catch (err) {
      console.error("Error fetching mapped reports:", err);
    } finally {
      setLoadingMappedReports(false);
    }
  };

  const handleCloseMapReportModal = () => {
    setShowMapReportModal(false);
    setSelectedPersonaForMapping(null);
    setSelectedReportIds([]);
    setMapReportSearchText("");
  };

  const handleToggleReport = (reportId) => {
    setSelectedReportIds((prevIds) =>
      prevIds.includes(reportId)
        ? prevIds.filter((id) => id !== reportId)
        : [...prevIds, reportId],
    );
  };

  const filteredAvailableReports = availableReports.filter((report) =>
    report.name.toLowerCase().includes(mapReportSearchText.toLowerCase()),
  );

  const handleSelectAllVisibleReports = () => {
    const visibleIds = filteredAvailableReports.map((report) => report.id);
    setSelectedReportIds((prevIds) => {
      const merged = new Set([...prevIds, ...visibleIds]);
      return Array.from(merged);
    });
  };

  const handleClearVisibleReports = () => {
    const visibleIds = new Set(
      filteredAvailableReports.map((report) => report.id),
    );
    setSelectedReportIds((prevIds) =>
      prevIds.filter((reportId) => !visibleIds.has(reportId)),
    );
  };

  const handleSaveReportMapping = async (event) => {
    event.preventDefault();
    if (!selectedPersonaForMapping) return;
    try {
      setSavingMapping(true);
      await saveReportMapping(
        selectedPersonaForMapping.name,
        selectedReportIds,
      );
      await loadPersonas();
      handleCloseMapReportModal();
      showToast("Reports mapped successfully!");
    } catch (err) {
      showToast("Error saving report mapping: " + err.message, "error");
    } finally {
      setSavingMapping(false);
    }
  };

  return (
    <>
      <div>
        <NavHeader />
        <SideMenu />
        <div style={{ position: "relative" }} id="main">
          <PageHeader
            title="Persona Administration"
            subtitle="Manage and register personas for your application."
          />

          <div className="pb-4">
            <TabStrip
              tabs={[
                { label: "Persona Management", value: "management" },
                { label: "Persona Registration", value: "registrations" },
              ]}
              active={activeTab}
              onChange={setActiveTab}
            />
          </div>

          {error && (
            <div className="container-fluid pb-4">
              <div
                className="alert alert-warning alert-dismissible fade show"
                role="alert"
              >
                <strong>Warning:</strong> {error}
                <AppButton
                  variant="outline"
                  onClick={() => setError(null)}
                  style={{ padding: "2px 10px", float: "right", lineHeight: 1 }}
                  title="Dismiss"
                >
                  ×
                </AppButton>
              </div>
            </div>
          )}

          {loading && activeTab === "management" ? (
            <div className="pb-4">
              <RankedTable
                columns={[
                  { label: "Persona Name" },
                  { label: "Created By" },
                  { label: "Created At" },
                  { label: "Actions", align: "center" },
                ]}
                rows={Array.from({ length: 6 }, () => ({ _skeleton: true }))}
                emptyText=""
                renderRow={() => (
                  <>
                    {[1, 2, 3, 4].map((j) => (
                      <td key={j} className="px-5 py-3">
                        <div
                          style={{
                            height: "13px",
                            background: "#f1f5f9",
                            borderRadius: "6px",
                            width: j === 4 ? "60%" : "85%",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </td>
                    ))}
                  </>
                )}
              />
            </div>
          ) : activeTab === "management" ? (
            <PersonaManagementTable
              searchText={searchText}
              setSearchText={setSearchText}
              filteredPersonas={filteredPersonas}
              onDeletePersona={handleDeletePersona}
              onEditPersona={handleEditPersona}
              onAddReport={handleAddReport}
            />
          ) : (
            <PersonaRegistrationsForm
              onCreatePersona={handleCreatePersona}
              loading={creatingPersona}
            />
          )}

          <SingleFieldEditModal
            open={showEditModal && !!editPersonaData}
            title="Edit Persona"
            label="Persona Name"
            value={editPersonaData?.name || ""}
            placeholder="Enter Persona Name"
            onChange={(e) =>
              setEditPersonaData((prev) => ({ ...prev, name: e.target.value }))
            }
            onSubmit={handleUpdatePersona}
            onClose={handleCloseEditModal}
            loading={updatingPersona}
          />

          <MapReportsModal
            open={showMapReportModal && !!selectedPersonaForMapping}
            personaName={selectedPersonaForMapping?.name}
            searchText={mapReportSearchText}
            onSearchChange={(e) => setMapReportSearchText(e.target.value)}
            selectedCount={selectedReportIds.length}
            onSelectAll={handleSelectAllVisibleReports}
            onClearAll={handleClearVisibleReports}
            reports={filteredAvailableReports}
            loading={loadingMappedReports}
            selectedIds={selectedReportIds}
            onToggle={handleToggleReport}
            onSubmit={handleSaveReportMapping}
            onClose={handleCloseMapReportModal}
            submitting={savingMapping}
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Persona"
        message="Are you sure you want to delete this persona? This action cannot be undone."
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        loading={deletingPersona}
        onConfirm={handleConfirmDelete}
        onClose={() => { if (!deletingPersona) setConfirmDialog({ open: false, personaId: null }); }}
      />
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default PersonaAdministration;
